import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import Footer from './Footer';

const faqs = [
  {
    question: "Wie funktioniert Plottie?",
    answer: "Du beantwortest ein paar einfache Fragen über dein Kind, und wir erstellen daraus eine personalisierte Geschichte. Die fertige Hörgeschichte kannst du dann direkt abspielen, herunterladen oder auf einen Kreativ-Tonie übertragen."
  },
  {
    question: "Was kostet Plottie?", 
    answer: "Eine personalisierte Hörgeschichte kostet 3,90€. Damit erhältst du eine einzigartige Geschichte, die genau auf dein Kind zugeschnitten ist. Hör mal in unserer Beispiel-Hörgeschichte!"
  },
  {
    question: "Kann ich meine Geschichte vor dem Kauf lesen?",
    answer: "Ja, du erhältst eine Vorschau deiner Geschichte nach der Beantwortung der Fragen. So kannst du dir ein genaues Bild machen, worum es in der Geschichte geht und ob sie deinem Kind gefallen wird."
  },
  {
    question: "Was passiert, wenn mir die Vorschau nicht gefällt?",
    answer: "Kein Problem! Du kannst einmalig kostenlos eine neue Vorschau von Plottie erstellen lassen. So erhältst du eine Alternative mit einer anderen spannenden Handlung, die vielleicht besser zu deinen Vorstellungen passt."
  },
  {
    question: "Wie schnell ist meine Hörgeschichte fertig?",
    answer: "Die Erstellung einer Geschichte dauert nur wenige Minuten. Sobald du die Fragen beantwortet hast, machen wir uns daran, deine Geschichte umgehend zu erstellen."
  },
  {
    question: "Funktioniert Plottie mit meiner Tonie-box?",
    answer: "Ja! Plottie-Hörgeschichten sind kompatibel mit Kreativ-Tonies. Du bekommst eine .mp3 Datei, die du wie gewohnt von der Tonie-Cloud auf deine Kreativ-Tonies übertragen kannst."
  },
  {
    question: "Wie lang sind die Hörgeschichten?",
    answer: "Unsere Hörgeschichten sind aktuell etwa 5 Minuten lang – perfekt für eine kleine Auszeit zwischendurch oder als Teil eurer Gute-Nacht-Routine. Wir arbeiten bereits daran, in Zukunft auch längere Geschichten mit mehreren Kapiteln anzubieten. Genau danach suchst du? Schreib uns gerne eine E-Mail an hi@plottie.de!"
  },
  {
    question: "Für welches Alter sind die Geschichten gedacht?",
    answer: "Gleich zu Beginn fragen wir nach dem Alter deines Kindes, damit wir die Geschichte optimal darauf abstimmen können. So stellen wir sicher, dass sowohl die Erzählweise als auch der Inhalt genau dem Entwicklungsstand deines Kindes entsprechen."
  },
  {
    question: "Bleiben meine Geschichten dauerhaft verfügbar?",
    answer: "Ja! Nach dem Kauf kannst du ganz einfach einen kostenlosen Plottie-Account erstellen. Damit hast du alle deine Geschichten immer griffbereit – zum Anhören, Herunterladen oder erneuten Übertragen auf deine Kreativ-Tonies."
  }
];

const FAQAccordion = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-[#343434]/10"
    >
      <button
        className="w-full py-6 flex justify-between items-center text-left group"
        onClick={onClick}
      >
        <h3 className="font-freeman text-2xl md:text-[2rem] mb-2 text-[#343434] leading-[110%] tracking-[-0.03rem] md:tracking-[-0.05rem]">{question}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <ChevronDown className="w-5 h-5 text-[#343434]" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
            className="overflow-hidden"
          >
            <div className="pb-8 text-[#343434] text-lg leading-relaxed max-w-[70ch] break-words font-aleo">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

function FAQPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* FAQ Section */}
      <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto pt-32 md:pt-40 pb-20">
        <div className="max-w-full mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:gap-16 lg:items-start">
            <div className="mb-16 lg:mb-0 lg:w-[40%] lg:sticky lg:top-32">
              <h2 className="text-[#7785FC] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] mb-2">
                HIER SIND ANTWORTEN
              </h2>
              <h3 className="text-[#343434] text-[2.125rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] font-freeman">
                Das möchten andere <br /> Eltern wissen
              </h3>
            </div>
            
            <div className="lg:w-[60%]">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  >
                    <FAQAccordion
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFaqIndex === index}
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FAQPage;